import { ZonedDateTime } from '@internationalized/date';

/**
 * Formats a number to a human-readable string with K, M, B suffixes
 * Examples: 1234 → 1.2K, 1234567 → 1.2M, 1234567890 → 1.2B
 */
export function formatNumber(value: number): string {
  if (value < 1000) {
    return value.toString();
  }

  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const magnitude = Math.floor(Math.log10(value) / 3);
  const suffix = suffixes[Math.min(magnitude, suffixes.length - 1)];
  const scaled = value / Math.pow(1000, magnitude);

  // Format to 1 decimal place if it has decimals, otherwise no decimals
  // For values like 999.99... that round to 1000, show as integer
  const rounded = Math.round(scaled * 10) / 10;
  const formatted = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);

  return `${formatted}${suffix}`;
}

/**
 * Calculates interval in milliseconds based on visible duration for internal rendering
 * This matches the pattern used in bars and skeleton components for consistent interval calculation
 */
export function getHistogramBarInterval(visibleDurationMs: number): number {
  const visibleMinutes = visibleDurationMs / (1000 * 60);

  // Determine interval based on duration (simplified version)
  // 15 minutes or less
  if (visibleMinutes <= 15) {
    return (1 / 6) * 60 * 1000; // 10 seconds
  }
  // 1 hour or less
  if (visibleMinutes <= 60) {
    return 0.5 * 60 * 1000; // 30 seconds
  }
  // 4 hours or less
  if (visibleMinutes <= 4 * 60) {
    return 2 * 60 * 1000; // 2 minutes
  }
  // 1 day or less
  if (visibleMinutes <= 24 * 60) {
    return 10 * 60 * 1000; // 10 minutes
  }
  // 2 days or less
  if (visibleMinutes <= 2 * 24 * 60) {
    return 20 * 60 * 1000; // 20 minutes
  }
  // 3 days or less
  if (visibleMinutes <= 3 * 24 * 60) {
    return 30 * 60 * 1000; // 30 minutes
  }
  // 7 days or less
  if (visibleMinutes <= 7 * 24 * 60) {
    return 120 * 60 * 1000; // 2 hours
  }
  // 15 days or less
  if (visibleMinutes <= 15 * 24 * 60) {
    return 240 * 60 * 1000; // 4 hours
  }
  // 31 days or less (about 1 month)
  if (visibleMinutes <= 31 * 24 * 60) {
    return 480 * 60 * 1000; // 8 hours
  }

  // For more than 1 month, extrapolate: each month (31 days) adds 8 hours to the interval
  const days = visibleMinutes / (24 * 60);
  const months = Math.ceil(days / 31);
  const intervalHours = 8 * months;
  return intervalHours * 60 * 60 * 1000;
}

/**
 * Returns the recommended bar interval in milliseconds based on the time range
 * Based on the histogram documentation recommendations
 */
export function getHistogramBarIntervalForRange(
  startTime: ZonedDateTime,
  endTime: ZonedDateTime,
): number {
  const durationMs = endTime.toDate().getTime() - startTime.toDate().getTime();
  return getHistogramBarInterval(durationMs);
}
